import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchLeagueView, getUserData, saveMatchResult, fetchH2H, freezeLeagueParticipation, unfreezeLeagueParticipation } from '../../api/api';
import LeagueCountdown from './LeagueCountdown';
import LeagueGroupTable from './LeagueGroupTable';
import MatchesTable from '../CompetitionSystems/MatchesTable/MatchesTable';
import MatchResultModal from '../CompetitionSystems/MatchResultModal/MatchResultModal';
import H2HModal from '../CompetitionSystems/H2HModal/H2HModal';
import CustomModal from '../../UI/Modal/CustomModal';
import Spinner from '../Spinner/Spinner';
import styles from './LeagueView.module.css';

const LeagueView = ({ tournament, isArchived, onReturn }) => {
    const navigate = useNavigate();
    const [leagueData, setLeagueData] = useState(null);
    const [selectedRound, setSelectedRound] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [userData, setUserData] = useState({});
    const [editingMatchId, setEditingMatchId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [isH2hModalOpen, setIsH2hModalOpen] = useState(false);
    const [h2hMatch, setH2hMatch] = useState(null);
    const [h2hData, setH2hData] = useState([]);
    const [isH2hLoading, setIsH2hLoading] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isFreezeConfirmOpen, setIsFreezeConfirmOpen] = useState(false);

    useEffect(() => {
        const fetchUser = async () => {
            const data = await getUserData();
            setUserData(data);
        };
        fetchUser();
    }, []);

    const loadData = async (roundNum) => {
        setIsLoading(true);
        const result = await fetchLeagueView(tournament.id, roundNum);
        if (result.success) {
            setLeagueData(result.data);
            if (selectedRound === null) {
                setSelectedRound(result.data.currentRoundNumber);
            }
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadData(selectedRound);
    }, [tournament.id, selectedRound]);

    const handleRoundChange = (e) => {
        setSelectedRound(parseInt(e.target.value));
    };

    const transitionIntervalRef = useRef(null);

    const handleRoundExpired = () => {
        if (isTransitioning) return;

        // Check if league is ending (next round would exceed end date)
        if (leagueData.config.leagueEndDate) {
            const endDate = new Date(leagueData.config.leagueEndDate);
            const nextRoundEnd = new Date();
            nextRoundEnd.setDate(nextRoundEnd.getDate() + leagueData.config.daysPerRound);
            if (nextRoundEnd > endDate) {
                // League is over, don't transition
                setModalMessage('Liga zakończona — ostatnia runda dobiegła końca.');
                setIsCustomModalOpen(true);
                return;
            }
        }

        setIsTransitioning(true);
        const expiredRound = leagueData?.currentRoundNumber;

        transitionIntervalRef.current = setInterval(async () => {
            const result = await fetchLeagueView(tournament.id, null);
            if (result.success && result.data.currentRoundNumber !== expiredRound) {
                clearInterval(transitionIntervalRef.current);
                setLeagueData(result.data);
                setSelectedRound(result.data.currentRoundNumber);
                setIsTransitioning(false);
            }
        }, 5000);
    };

    useEffect(() => {
        return () => {
            if (transitionIntervalRef.current) clearInterval(transitionIntervalRef.current);
        };
    }, []);

    const handleEditMatch = (matchId) => {
        setEditingMatchId(matchId);
        setIsModalOpen(true);
    };

    const onSaveResult = async (matchId, scores, walkover, walkoverWinnerId, injuryResignation, injuryResignationParticipantId, clearMatchData) => {
        const matchResult = {
            matchId, scores, walkover, walkoverWinnerId,
            injuryResignation, injuryResignationParticipantId, clearMatchData
        };
        const result = await saveMatchResult(matchResult);
        setIsModalOpen(false);
        if (result.success) {
            setModalMessage('Wynik zapisany pomyślnie');
            loadData(selectedRound);
        } else {
            setModalMessage('Błąd zapisu wyniku: ' + (result.data || ''));
        }
        setIsCustomModalOpen(true);
    };

    const handleH2H = async (matchId) => {
        const allM = leagueData.groupStandings.flatMap(g => g.matches);
        const match = allM.find(m => m.id === matchId);
        setH2hMatch(match || null);
        setIsH2hLoading(true);
        setIsH2hModalOpen(true);
        const result = await fetchH2H(matchId);
        if (Array.isArray(result)) {
            setH2hData(result);
        }
        setIsH2hLoading(false);
    };

    const handleFreeze = async () => {
        setIsFreezeConfirmOpen(false);
        const result = await freezeLeagueParticipation(tournament.id);
        if (result.success) {
            setModalMessage('Zamrożenie zgłoszone. Obowiązuje od następnej rundy.');
            loadData(selectedRound);
        } else {
            setModalMessage('Błąd: ' + (result.data || ''));
        }
        setIsCustomModalOpen(true);
    };

    const handleUnfreeze = async () => {
        const result = await unfreezeLeagueParticipation(tournament.id);
        if (result.success) {
            setModalMessage('Zamrożenie odwołane.');
            loadData(selectedRound);
        } else {
            setModalMessage('Błąd: ' + (result.data || ''));
        }
        setIsCustomModalOpen(true);
    };

    if (isLoading || !leagueData) return <Spinner />;

    const allMatches = leagueData.groupStandings.flatMap(g => g.matches);
    const editingMatch = allMatches.find(m => m.id === editingMatchId);
    const userRoles = new Set(userData.authorities || []);
    const isSupervisor = userRoles.has("ROLE_SUPERVISOR") || userRoles.has("ROLE_SUPER");

    const totalMatches = allMatches.length;
    const playedMatches = allMatches.filter(m => m.participantWinnerId !== null).length;
    const progressPercentage = totalMatches > 0 ? Math.round((playedMatches / totalMatches) * 100) : 0;
    const isParticipant = leagueData.freezesRemaining !== null && leagueData.freezesRemaining !== undefined;
    const canFreeze = isParticipant && !leagueData.frozenForNextRound && leagueData.freezesRemaining > 0;
    const isViewingPastRound = selectedRound !== leagueData.currentRoundNumber;

    return (
        <div className={styles.container}>
            {isTransitioning && (
                <div className={styles.transitionOverlay}>
                    <div className={styles.transitionContent}>
                        <p>Przejście do następnej rundy...</p>
                        <Spinner size="large" />
                    </div>
                </div>
            )}
            {isSupervisor && !isArchived && (
                <button
                    className={styles.adminButton}
                    onClick={() => navigate(`/admin-league/${tournament.id}`)}
                >
                    Panel admina ligi
                </button>
            )}
            <div className={styles.roundHeader}>
                <button
                    className={styles.rankingLink}
                    onClick={() => navigate(`/leagues/${tournament.id}/ranking`, {
                        state: {
                            returnPath: '/tournaments',
                            returnState: {
                                activeTab: isArchived ? 'archived' : 'current',
                                tournamentId: tournament.id
                            }
                        }
                    })}
                >
                    <span role="img" aria-label="ranking">🏆</span> Ranking i wyróżnienia
                </button>

                <div className={styles.roundSelector}>
                    <span className={styles.roundLabel}>Runda:</span>
                    <select className={styles.roundSelect} value={selectedRound || ''} onChange={handleRoundChange}>
                        {leagueData.rounds.map(r => (
                            <option key={r.roundNumber} value={r.roundNumber}>
                                {r.roundNumber}
                            </option>
                        ))}
                    </select>
                </div>

                <span className={styles.scoringInfo}>
                    Punktacja: {leagueData.config.scoringSystem === 'SYSTEM_3210' ? '3-2-1-0' : 'Klasyczna (2:0)'}
                </span>

                {!isArchived && leagueData.roundEndDate && selectedRound === leagueData.currentRoundNumber && leagueData.config.countdownEnabled !== false && (
                    <LeagueCountdown
                        startDate={leagueData.config.currentRoundStartDate}
                        endDate={leagueData.roundEndDate}
                        onExpired={handleRoundExpired}
                    />
                )}

                {totalMatches > 0 && leagueData.groupStandings.length > 1 && (
                    <div className={styles.progressWrapper}>
                        <div className={styles.progressBar}>
                            <div
                                className={styles.progressFill}
                                style={{
                                    width: `${progressPercentage}%`,
                                    backgroundColor: progressPercentage === 100 ? '#2196f3'
                                        : progressPercentage >= 67 ? '#4caf50'
                                        : progressPercentage >= 34 ? '#ffeb3b'
                                        : '#f44336',
                                }}
                            />
                        </div>
                        <div className={styles.progressText}>
                            {progressPercentage}% rozegranych ({playedMatches}/{totalMatches})
                        </div>
                    </div>
                )}
            </div>

            {isParticipant && !isArchived && leagueData.config.matchPlanningEnabled !== false && (
                <div className={styles.freezeSection}>
                    {leagueData.frozenForNextRound ? (
                        <button className={styles.unfreezeButton} onClick={handleUnfreeze}>
                            Odmróź udział
                        </button>
                    ) : canFreeze ? (
                        <button className={styles.freezeButton} onClick={() => setIsFreezeConfirmOpen(true)}>
                            ❄️ Zamróź na następną rundę (pozostało: {leagueData.freezesRemaining})
                        </button>
                    ) : null}
                </div>
            )}

            {isParticipant && !isArchived && !isViewingPastRound && leagueData.config.matchPlanningEnabled !== false && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 15 }}>
                    <button
                        className={styles.matchPlanningButton}
                        onClick={() => navigate(`/leagues/${tournament.id}/match-planning`)}
                    >
                        🎾 Planowanie meczów
                    </button>
                </div>
            )}

            <div className={styles.groupsGrid}>
                {leagueData.groupStandings.map((group, groupIndex) => (
                    <div key={group.groupLabel} className={styles.groupSection}>
                        {leagueData.groupStandings.length > 1 && (
                            <h3 className={styles.groupTitle}>
                                Grupa {group.groupLabel}
                                <span className={styles.groupWeight}> (premia grupowa: {group.groupPremium ?? 0})</span>
                            </h3>
                        )}
                        <LeagueGroupTable
                            standings={group.standings}
                            promoted={group.effectivePromoted}
                            relegated={group.effectiveRelegated}
                            compact={leagueData.groupStandings.length === 1}
                        />
                        <MatchesTable
                            matches={group.matches}
                            onEditMatch={handleEditMatch}
                            onOpenH2H={handleH2H}
                            isArchived={isArchived || isViewingPastRound}
                            showBallProvider={isParticipant && !isViewingPastRound && !isArchived && leagueData.config.matchPlanningEnabled !== false}
                        />
                    </div>
                ))}
            </div>

            {isModalOpen && editingMatch && (
                <MatchResultModal
                    isOpen={isModalOpen}
                    onRequestClose={() => { setIsModalOpen(false); setEditingMatchId(null); }}
                    onSaveResult={onSaveResult}
                    editingMatch={editingMatch}
                    matchId={editingMatchId}
                    participant1Id={editingMatch.participant1Id}
                    participant2Id={editingMatch.participant2Id}
                    participant1FirstName={editingMatch.participant1FirstName}
                    participant1LastName={editingMatch.participant1LastName}
                    participant2FirstName={editingMatch.participant2FirstName}
                    participant2LastName={editingMatch.participant2LastName}
                    participant1PartnerFirstName={editingMatch.participant1PartnerFirstName}
                    participant1PartnerLastName={editingMatch.participant1PartnerLastName}
                    participant2PartnerFirstName={editingMatch.participant2PartnerFirstName}
                    participant2PartnerLastName={editingMatch.participant2PartnerLastName}
                    isThirdSetTiebreak={tournament.isThirdSetTiebreak}
                />
            )}

            <CustomModal
                isOpen={isCustomModalOpen}
                onRequestClose={() => setIsCustomModalOpen(false)}
                message={modalMessage}
                isPositiveMessage={modalMessage.includes("pomyślnie") || modalMessage.includes("zrobione") || modalMessage.includes("Zamrożenie") || modalMessage.includes("odwołane")}
                cancelLabel="Zamknij"
                cancelButtonColor="green"
                showConfirmButton={false}
            />

            <CustomModal
                isOpen={isFreezeConfirmOpen}
                onRequestClose={() => setIsFreezeConfirmOpen(false)}
                message={`Zamrożenie obowiązuje od następnej rundy na 1 rundę. Awanse i spadki zostaną naliczone normalnie. Pozostało zamrożeń: ${leagueData.freezesRemaining}.`}
                isPositiveMessage={true}
                confirmLabel="Zamróź"
                cancelLabel="Anuluj"
                showConfirmButton={true}
                onConfirm={handleFreeze}
            />

            {isH2hModalOpen && (
                <H2HModal
                    isOpen={isH2hModalOpen}
                    onRequestClose={() => { setIsH2hModalOpen(false); setH2hMatch(null); setH2hData([]); }}
                    match={h2hMatch}
                    h2hData={h2hData}
                    isLoading={isH2hLoading}
                />
            )}
        </div>
    );
};

export default LeagueView;
